import ApiDef from "./ApiDef";
import {
	ApiResponse,
	Body,
	EventResult,
	Params,
	Query,
	RequestCacheInfo,
	RequestConfig,
	RequestContextStats,
	RequestError,
	RequestEvent,
	RequestEventHandlers,
	RequestHost,
	RequestMethod,
	ResponseType,
} from "./ApiTypes";
import * as EnvironmentUtils from "../util/EnvironmentUtils";
import * as StringUtils from "../util/StringUtils";
import { queryStringify } from "./ApiUtils";

export default class RequestContext<
	R = any,
	P extends Params | undefined = Params | undefined,
	Q extends Query | undefined = Query | undefined,
	B extends Body | undefined = Body | undefined,
> {
	readonly id: string;
	readonly key: string;
	readonly computedPath: string;
	readonly stats: RequestContextStats;
	private readonly host: RequestHost;
	readonly eventHandlers: RequestEventHandlers<R>;

	private canceler: (() => void) | null = null;
	response: ApiResponse<R> | null = null;
	error: RequestError | null = null;
	readonly cacheInfo: RequestCacheInfo = { cached: false, source: null };

	cancelled: boolean = false;
	readonly computedConfig: RequestConfig<R, P, Q, B>;

	constructor(
		host: RequestHost,
		config: RequestConfig<R, P, Q, B>,
		computedPath: string,
	) {
		this.id = StringUtils.uuid(8);
		this.host = host;
		this.computedConfig = config;
		Object.assign(this.computedConfig.headers, { "Request-Id": this.id });
		this.parseHeaders();
		this.computedPath = computedPath;

		this.key = this.generateKey();
		this.stats = {
			attempt: 0,
			cached: false,
		};
		this.eventHandlers = {};
		this.initMiddleware();
	}

	get method(): RequestMethod {
		return this.host.method;
	}

	get api(): ApiDef {
		return this.host.api;
	}

	get baseUrl(): string {
		return this.host.baseUrl;
	}

	get responseType(): ResponseType {
		return this.host.responseType;
	}

	initMiddleware() {
		const { middleware } = this.api;
		for (let i = 0; i < middleware.length; i++) {
			const events = middleware[i];
			const eventTypes = Object.keys(events);
			for (let n = 0; n < eventTypes.length; n++) {
				const eventType = eventTypes[n] as RequestEvent;
				const eventHandlersForType =
					this.eventHandlers[eventType] || (this.eventHandlers[eventType] = []);
				const middlewareEventHandlers = events[eventType];
				if (middlewareEventHandlers) {
					eventHandlersForType.push(middlewareEventHandlers);
				}
			}
		}
	}

	private generateKey() {
		const { computedConfig } = this;
		let string = this.computedPath.trim();
		let bodyString = "";
		if (computedConfig.body) {
			bodyString = JSON.stringify(computedConfig.body);
		}
		if (bodyString.length > 0) {
			string += "|" + StringUtils.hashCode(bodyString);
		}
		const queryString =
			computedConfig.query && queryStringify(computedConfig.query);
		if (queryString) {
			string += "?" + queryString;
		}

		const app = EnvironmentUtils.getAppInfo();
		const version = app.ci ? app.build!.commit.substring(0, 12) : "DEV";
		return `${version}#${string}`;
	}

	updateHeaders(newHeaders: Record<string, string>): this {
		this.computedConfig.headers = Object.assign(
			this.computedConfig.headers || {},
			newHeaders,
		);
		this.parseHeaders();
		return this;
	}

	private parseHeaders() {
		this.computedConfig.headers = Object.keys(
			this.computedConfig.headers as any,
		).reduce((parsedHeaders, key) => {
			const value = (this.computedConfig.headers as any)[key];
			if (value !== undefined) {
				parsedHeaders[key] = value;
			}
			return parsedHeaders;
		}, {} as any);
	}

	updateQuery(newQuery: Partial<Q>): this {
		this.computedConfig.query = Object.assign(
			this.computedConfig.query || {},
			newQuery,
		);
		return this;
	}

	async triggerEvent(
		eventType: RequestEvent,
	): Promise<EventResult<R> | undefined> {
		const eventHandlers = this.eventHandlers[eventType];
		if (eventHandlers) {
			for (let i = 0; i < eventHandlers.length; i++) {
				const eventHandler = eventHandlers[i];
				// eslint-disable-next-line no-await-in-loop
				const eventResult = await eventHandler(this as RequestContext);
				if (eventResult) {
					return eventResult;
				}
			}
		}
		return undefined;
	}

	addCanceller(canceler: () => void) {
		this.canceler = canceler;
	}

	cancel() {
		this.cancelled = true;
		if (this.canceler) {
			this.canceler();
		}
	}
}
